/**
 * The app's sections, in sidebar order. `requirements` lists the ids from
 * docs/requirements.md that each section delivers. Subpages live under their
 * section's path, so the section stays highlighted on them.
 */
export type Section = {
  path: string;
  label: string;
  summary: string;
  requirements: string[];
  /** A short tag beside the label, for sections planned for a later version. */
  tag?: string;
};

const ALL_SECTIONS: Section[] = [
  {
    path: '/overview',
    label: 'Overview',
    summary: 'What needs the team today: due follow-ups, chats to confirm, AI suggestions to review, and how the pipeline moves.',
    requirements: ['REM-1', 'TG-2', 'CAS-5', 'LEAD-1…6'],
  },
  {
    path: '/leads',
    label: 'Leads',
    summary: 'Everyone who registered, wrote to us or joined a Telegram group, with their source, stage and the latest AI summary.',
    requirements: ['LEAD-1…6', 'TG-2…5', 'CAS-5', 'ATT-1…5'],
  },
  {
    path: '/follow-ups',
    label: 'Follow-ups',
    summary: 'Every follow-up the team has set on a lead: due, coming up and done.',
    requirements: ['REM-1'],
  },
  {
    path: '/conversations',
    label: 'Conversations',
    summary: 'Telegram groups and direct messages with the bot, and later email and Instagram: confirm who each client is, and read the conversation.',
    requirements: ['TG-1…8', 'LEAD-6', 'ATT-2'],
  },
  {
    path: '/casinos',
    label: 'Casinos',
    summary: 'Every launched casino, mirrored from Owner Hub, with its owner and performance.',
    requirements: ['CAS-1…4', 'SEG-1'],
  },
  {
    path: '/segments',
    label: 'Segments',
    summary: 'Owner groups by performance (GGR, players, velocity, RFM) and by geography or type.',
    requirements: ['SEG-1…3'],
  },
  {
    path: '/messaging',
    label: 'Messaging',
    summary: 'One-off and automated updates to leads and casino owners by email, Telegram and Owner Studio notifications.',
    requirements: ['OUT-1…6', 'TG-6', 'SEG-3'],
  },
  {
    path: '/calls',
    label: 'Calls',
    summary: 'Calls with clients, linked to the lead automatically, with the note taker’s notes.',
    requirements: ['CALL-1…4'],
    tag: 'V2',
  },
  {
    path: '/knowledge',
    label: 'Knowledge base',
    summary: 'What the bot knows, and the short answers it gave clients on Telegram and by email.',
    requirements: ['REM-2', 'REM-3'],
  },
  {
    path: '/reports',
    label: 'Reports',
    summary: 'Where leads come from, how they move from lead to sale, and which campaigns bring them.',
    requirements: ['ATT-1…5', 'LEAD-4'],
  },
];

/** Whether the features planned for V2 show (window.config.showV2). */
export const showV2 = Boolean(window.config.showV2);

/**
 * Whether the made-up parts of pages not built yet show (window.config.showDemo): only
 * locally. Elsewhere such a part says "No data yet", and a tab keeps its place.
 */
export const showDemo = Boolean(window.config.showDemo);

/** The sections in the sidebar: the V2 ones only when they are switched on. */
export const SECTIONS: Section[] = ALL_SECTIONS.filter((s) => showV2 || s.tag !== 'V2');

/** Settings sits apart, at the bottom of the sidebar. */
export const SETTINGS: Section = {
  path: '/settings',
  label: 'Settings',
  summary: 'Staff, the Telegram bot, outbound channels and the Owner Hub sync.',
  requirements: ['TG-6', 'OUT-1', 'CAS-2'],
};
